import { ThreadDto } from '@/api/generated';

export const RESPONSE_REGEX = /<response>([\s\S]*?)<\/response>/;
export const THOUGHTS_REGEX = /<thoughts>([\s\S]*?)<\/thoughts>/;

export const extractResponse = (content: string): string => {
  const responseMatch = content.match(RESPONSE_REGEX);
  return responseMatch?.[1]?.trim() || '';
};

export const extractThoughts = (content: string): string => {
  const thoughtsMatch = content.match(THOUGHTS_REGEX);
  return thoughtsMatch?.[1]?.trim() || '';
};

type StreamContext = {
  streamBufferRef: React.MutableRefObject<string>;
  isInThoughtsRef: React.MutableRefObject<boolean>;
  isInResponseRef: React.MutableRefObject<boolean>;
  thoughtsAccumulatorRef: React.MutableRefObject<string>;
  responseAccumulatorRef: React.MutableRefObject<string>;
  setStreamingThoughts: React.Dispatch<React.SetStateAction<string[]>>;
  setStreamingResponse: React.Dispatch<React.SetStateAction<string>>;
};

export const handleStreamingChunk = (chunk: { content: string }, ctx: StreamContext) => {
  const {
    streamBufferRef,
    isInThoughtsRef,
    isInResponseRef,
    thoughtsAccumulatorRef,
    responseAccumulatorRef,
    setStreamingThoughts,
    setStreamingResponse
  } = ctx;

  streamBufferRef.current += chunk.content;

  // Detect <thoughts>
  if (streamBufferRef.current.includes('<thoughts>')) {
    isInThoughtsRef.current = true;
    thoughtsAccumulatorRef.current = '';
    streamBufferRef.current = streamBufferRef.current.split('<thoughts>').pop() || '';
  }

  // If we're inside a thoughts block, keep updating as new content arrives
  if (isInThoughtsRef.current) {
    if (streamBufferRef.current.includes('</thoughts>')) {
      const [thoughtsText, rest] = streamBufferRef.current.split('</thoughts>');
      thoughtsAccumulatorRef.current += thoughtsText;
      setStreamingThoughts([thoughtsAccumulatorRef.current.trim()]);
      isInThoughtsRef.current = false;
      streamBufferRef.current = rest;
      thoughtsAccumulatorRef.current = '';
    } else {
      thoughtsAccumulatorRef.current += streamBufferRef.current;
      setStreamingThoughts([thoughtsAccumulatorRef.current]);
      streamBufferRef.current = '';
    }
  }

  // Detect <response>
  if (!isInResponseRef.current && streamBufferRef.current.includes('<response>')) {
    isInResponseRef.current = true;
    responseAccumulatorRef.current = '';
    streamBufferRef.current = streamBufferRef.current.split('<response>').pop() || '';
  }

  // If we're inside a response, keep updating as new content arrives
  if (isInResponseRef.current) {
    if (streamBufferRef.current.includes('</response>')) {
      const [responseText, rest] = streamBufferRef.current.split('</response>');
      responseAccumulatorRef.current += responseText;
      setStreamingResponse(responseAccumulatorRef.current.trim());
      isInResponseRef.current = false;
      streamBufferRef.current = rest;
      responseAccumulatorRef.current = '';
    } else {
      responseAccumulatorRef.current += streamBufferRef.current;
      setStreamingResponse(responseAccumulatorRef.current);
      streamBufferRef.current = '';
    }
  }
};

export const findThreadToSetActive = (
  flatThreads: ThreadDto[],
  activeThreadId: string | null
): ThreadDto => {
  if (!activeThreadId) return flatThreads[0];

  const activeThreadIndex = flatThreads.findIndex(thread => thread.id === activeThreadId);

  if (activeThreadIndex === 0) return flatThreads[1];
  if (activeThreadIndex === -1) return flatThreads[0];
  return flatThreads[activeThreadIndex - 1];
};
